import { useState, useEffect } from 'react'
import { Button } from '../../../../components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../../components/ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select'
import { Switch } from '@mui/material'
import { Plus, Trash2 } from 'lucide-react'

export type ConfigTemplateFormData = {
    name: string
    label: string
    description: string
    dataType: string
    groupName: string
    sortOrder: string
    required: boolean
    defaultValue: string
    options: {
        min: string
        max: string
        step: string
        trueLabel: string
        falseLabel: string
        choices: { value: string; label: string }[]
        formatHint: string
    }
}

const defaultFormData: ConfigTemplateFormData = {
    name: '',
    label: '',
    description: '',
    dataType: 'string',
    groupName: '',
    sortOrder: '',
    required: false,
    defaultValue: '',
    options: {
        min: '',
        max: '',
        step: '',
        trueLabel: '',
        falseLabel: '',
        choices: [],
        formatHint: '',
    },
}

type ConfigTemplateDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSave: (data: ConfigTemplateFormData) => void
    initialData?: any
    isPending: boolean
}

export const ConfigTemplateDialog = ({
    open,
    onOpenChange,
    onSave,
    initialData,
    isPending,
}: ConfigTemplateDialogProps) => {
    const [form, setForm] = useState<ConfigTemplateFormData>(defaultFormData)
    const isEditing = !!initialData

    useEffect(() => {
        if (open) {
            if (initialData) {
                const opts = initialData.options || {}
                setForm({
                    name: initialData.name || '',
                    label: initialData.label || '',
                    description: initialData.description || '',
                    dataType: initialData.dataType || 'string',
                    groupName: initialData.groupName || '',
                    sortOrder: initialData.sortOrder != null ? String(initialData.sortOrder) : '',
                    required: initialData.required || false,
                    defaultValue: initialData.defaultValue != null ? String(initialData.defaultValue) : '',
                    options: {
                        min: opts.min != null ? String(opts.min) : '',
                        max: opts.max != null ? String(opts.max) : '',
                        step: opts.step != null ? String(opts.step) : '',
                        trueLabel: opts.trueLabel || '',
                        falseLabel: opts.falseLabel || '',
                        choices: opts.choices || [],
                        formatHint: opts.formatHint || '',
                    },
                })
            } else {
                setForm(defaultFormData)
            }
        }
    }, [open, initialData])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        onSave(form)
    }

    const updateField = (field: keyof ConfigTemplateFormData, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const updateOption = (field: string, value: any) => {
        setForm((prev) => ({
            ...prev,
            options: { ...prev.options, [field]: value },
        }))
    }

    const addChoice = () => {
        updateOption('choices', [...form.options.choices, { value: '', label: '' }])
    }

    const removeChoice = (index: number) => {
        updateOption(
            'choices',
            form.options.choices.filter((_, i) => i !== index)
        )
    }

    const updateChoice = (index: number, field: 'value' | 'label', val: string) => {
        const updated = [...form.options.choices]
        updated[index] = { ...updated[index], [field]: val }
        updateOption('choices', updated)
    }

    const inputClass =
        'h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring'
    const labelClass = 'text-xs font-medium text-muted-foreground uppercase tracking-wide'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Config Template' : 'Add Config Template'}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Name *</label>
                            <input
                                value={form.name}
                                onChange={(e) => updateField('name', e.target.value)}
                                required
                                placeholder="e.g. plc_version"
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Label *</label>
                            <input
                                value={form.label}
                                onChange={(e) => updateField('label', e.target.value)}
                                required
                                placeholder="e.g. PLC Version"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className={labelClass}>Description</label>
                        <input
                            value={form.description}
                            onChange={(e) => updateField('description', e.target.value)}
                            placeholder="Optional description"
                            className={inputClass}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Data Type *</label>
                            <Select
                                value={form.dataType}
                                onValueChange={(v) => updateField('dataType', v)}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="string">String</SelectItem>
                                    <SelectItem value="number">Number</SelectItem>
                                    <SelectItem value="boolean">Boolean</SelectItem>
                                    <SelectItem value="enum">Enum</SelectItem>
                                    <SelectItem value="version">Version</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Group Name</label>
                            <input
                                value={form.groupName}
                                onChange={(e) => updateField('groupName', e.target.value)}
                                placeholder="e.g. Software"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Sort Order</label>
                            <input
                                type="number"
                                value={form.sortOrder}
                                onChange={(e) => updateField('sortOrder', e.target.value)}
                                placeholder="0"
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Default Value</label>
                            <input
                                value={form.defaultValue}
                                onChange={(e) => updateField('defaultValue', e.target.value)}
                                placeholder="Optional"
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-1">
                        <div className="space-y-0.5">
                            <p className="text-sm font-medium text-foreground">Required</p>
                            <p className="text-xs text-muted-foreground">
                                Mark this setting as required for devices
                            </p>
                        </div>
                        <Switch
                            checked={form.required}
                            onChange={(e) => updateField('required', e.target.checked)}
                            size="small"
                        />
                    </div>

                    {form.dataType === 'number' && (
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-1.5">
                                <label className={labelClass}>Min</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={form.options.min}
                                    onChange={(e) => updateOption('min', e.target.value)}
                                    placeholder="Min"
                                    className={inputClass}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Max</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={form.options.max}
                                    onChange={(e) => updateOption('max', e.target.value)}
                                    placeholder="Max"
                                    className={inputClass}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Step</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={form.options.step}
                                    onChange={(e) => updateOption('step', e.target.value)}
                                    placeholder="Step"
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    )}

                    {form.dataType === 'boolean' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className={labelClass}>True Label</label>
                                <input
                                    value={form.options.trueLabel}
                                    onChange={(e) => updateOption('trueLabel', e.target.value)}
                                    placeholder="e.g. Enabled"
                                    className={inputClass}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>False Label</label>
                                <input
                                    value={form.options.falseLabel}
                                    onChange={(e) => updateOption('falseLabel', e.target.value)}
                                    placeholder="e.g. Disabled"
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    )}

                    {form.dataType === 'enum' && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <label className={labelClass}>Choices</label>
                                <button
                                    type="button"
                                    onClick={addChoice}
                                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                                >
                                    <Plus className="h-3 w-3" />
                                    Add Choice
                                </button>
                            </div>
                            {form.options.choices.map((choice, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input
                                        value={choice.value}
                                        onChange={(e) => updateChoice(i, 'value', e.target.value)}
                                        placeholder="Value"
                                        className={inputClass}
                                    />
                                    <input
                                        value={choice.label}
                                        onChange={(e) => updateChoice(i, 'label', e.target.value)}
                                        placeholder="Label"
                                        className={inputClass}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeChoice(i)}
                                        className="h-7 w-7 shrink-0 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {form.dataType === 'version' && (
                        <div className="space-y-1.5">
                            <label className={labelClass}>Format Hint</label>
                            <input
                                value={form.options.formatHint}
                                onChange={(e) => updateOption('formatHint', e.target.value)}
                                placeholder="e.g. major.minor.patch"
                                className={inputClass}
                            />
                        </div>
                    )}

                    <DialogFooter className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending}>
                            {isPending
                                ? 'Saving...'
                                : isEditing
                                  ? 'Save Changes'
                                  : 'Add Template'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
